import React, { useCallback, useEffect, useState } from 'react';
import { Card, Descriptions, Button, Tag, Skeleton, Modal, Form, Select, Input, DatePicker, InputNumber, message } from 'antd';
import { LeftOutlined, CrownOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { useGetBreakpoints } from '../../hooks/useGetBreakpoints';
import GTMWrapper from "../../components/GTMWrapper"
import { axiosInstance, BASE_API_CONFIG } from '../../util/Api';
import { useSelector } from 'react-redux';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;

const UpdatefreightrateDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { state } = location;
  const { isMobile } = useGetBreakpoints();
  const authUser = useSelector(({ auth }) => auth.userData);
  const countries = useSelector(({ common }) => common.countries);
  
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const userId = authUser?.id;

  // Modal states
  const [isBookingModalVisible, setIsBookingModalVisible] = useState(false);
  const [isNegotiableModalVisible, setIsNegotiableModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [negotiableLoading, setNegotiableLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingForm] = Form.useForm();
  const [negotiableForm] = Form.useForm();

  // Add shimmer animation for blur effect
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchUserDetails = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${BASE_API_CONFIG.baseURL}/users/${userId}`
      );
      console.log("User details from API:", response.data);
      setUserDetails(response.data?.data || response.data);
    } catch (err) {
      setError(err.message);
      console.error('User details fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  // Derive values from userDetails with safe defaults
  const subscriptionStatus = userDetails?.Subscription;
  const userBusinessType = userDetails?.businessTypes?.code;
  
  // Safe boolean checks - default to false while loading
  const isFreightForwarder = userBusinessType === 'freight_forwarder';
  const isExporter = userBusinessType === 'exporter' || userBusinessType === 'manufacturer_exporter' || userBusinessType === 'customhouseagents_freightforwarder';
  const isFreeUser = subscriptionStatus === 'Free';
  const isPremiumUser = subscriptionStatus === 'Premium' || subscriptionStatus === 'Paid';

  const freightRate = state?.freightRate;
  console.log("freightRatecheck>>>01", freightRate);
  const allFreightRates = state?.allFreightRates;
  console.log("allFreightRatescheck00", allFreightRates);

  const navigateBusinessCard = (userid) => {
    if (userid) {
      history.push(`/user-profile?id=${userid}`);
    }
  };

  const goToProfilePage = () => {
    history.push('/my-profile');
  };

  // Check if current logged-in user is exporter
  const isLoggedInUserExporter = authUser?.businessTypes?.code === 'exporter' || 
                                  authUser?.businessTypes?.code === 'manufacturer_exporter';

  // Handle Book Button
  const handleRequestToBook = useCallback((record) => {
    setSelectedRecord(record);
    setIsBookingModalVisible(true);
    
    bookingForm.setFieldsValue({
      productName: '',
      containerType: record.containerType || '',
      numberOfContainers: 1,
      netWeightPerContainer: '',
      cargoReadinessDate: null,
      specialRequirements: ''
    });
  }, [bookingForm]);

  // Handle Negotiable Button
  const handleRequestToNegotiate = useCallback((record) => {
    setSelectedRecord(record);
    setIsNegotiableModalVisible(true);
    
    negotiableForm.setFieldsValue({
      productName: '',
      containerType: record.containerType || '',
      numberOfContainers: 1,
      netWeightPerContainer: '',
      cargoReadinessDate: null,
      currency: 'USD',
      rateindication: '',
      specialRequirements: ''
    });
  }, [negotiableForm]);

  const handleBookingSubmit = async (values) => {
    try {
      setBookingLoading(true);
      
      const bookingData = {
        ...values,
        freightRateId: selectedRecord.id || selectedRecord.userid,
        loadingPort: selectedRecord.loadingPort,
        destinationPort: selectedRecord.destinationPort,
        freightRate: selectedRecord.oceanfreight,
        currency: selectedRecord.currency,
        companyName: selectedRecord.comapanyName,
        postedBy: selectedRecord.username,
        postedByUserId: selectedRecord.userid,
        requestedBy: authUser.name,
        requestedByUserId: authUser.id,
        requestedByCompany: authUser.companyName,
        cargoReadinessDate: values.cargoReadinessDate ? values.cargoReadinessDate.format('YYYY-MM-DD') : null,
        status: 'pending',
        bookingType: 'direct',
        createdAt: new Date().toISOString()
      };

      const response = await axiosInstance.post(
        `${BASE_API_CONFIG.baseURL}/updates-freight-rates/freight-bookings`,
        bookingData
      );

      if (response.status === 201) {
        message.success('Booking request submitted successfully!');
        setIsBookingModalVisible(false);
        bookingForm.resetFields();
        setSelectedRecord(null);
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      message.error('Failed to submit booking request. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleNegotiableSubmit = async (values) => {
    try {
      setNegotiableLoading(true);
      
      const negotiableData = {
        ...values,
        freightRateId: selectedRecord.id || selectedRecord.userid,
        loadingPort: selectedRecord.loadingPort,
        destinationPort: selectedRecord.destinationPort,
        freightRate: selectedRecord.oceanfreight,
        currency: selectedRecord.currency,
        companyName: selectedRecord.comapanyName,
        postedBy: selectedRecord.username,
        postedByUserId: selectedRecord.userid,
        requestedBy: authUser.name,
        requestedByUserId: authUser.id,
        requestedByCompany: authUser.companyName,
        cargoReadinessDate: values.cargoReadinessDate ? values.cargoReadinessDate.format('YYYY-MM-DD') : null,
        status: 'pending',
        bookingType: 'negotiable',
        rateindication: values.rateindication,
        rateindicationCurrency: values.currency,
        productName: values.productName,
        containerType: values.containerType,
        numberOfContainers: values.numberOfContainers,
        netWeightPerContainer: values.netWeightPerContainer,
        specialRequirements: values.specialRequirements,
        createdAt: new Date().toISOString()
      };

      const response = await axiosInstance.post(
        `${BASE_API_CONFIG.baseURL}/updates-freight-rates/freight-bookings`,
        negotiableData
      );

      if (response.status === 201) {
        message.success('Negotiation request submitted successfully!');
        setIsNegotiableModalVisible(false);
        negotiableForm.resetFields();
        setSelectedRecord(null);
      }
    } catch (error) {
      console.error('Negotiable submission error:', error);
      message.error('Failed to submit negotiation request. Please try again.');
    } finally {
      setNegotiableLoading(false);
    }
  };

  const handleBookingCancel = () => {
    setIsBookingModalVisible(false);
    bookingForm.resetFields();
    setSelectedRecord(null);
  };

  const handleNegotiableCancel = () => {
    setIsNegotiableModalVisible(false);
    negotiableForm.resetFields();
    setSelectedRecord(null);
  };

  // Check if current logged-in user is freight forwarder
  const isLoggedInUserFreightForwarder = authUser?.businessTypes?.code === 'freight_forwarder';

  // Company Name Display Component with Business Type logic
  const CompanyNameDisplay = ({ data }) => {
    const companyName = data?.comapanyName || data?.companyName || '-';
    const userId = data?.userid;

    // If logged-in user is freight forwarder, don't show anything (hide completely)
    if (isLoggedInUserFreightForwarder) {
      return null;
    }

    // Show loading skeleton while fetching user details
    if (loading) {
      return (
        <div style={{ width: '100%' }}>
          <Skeleton.Input active size="small" style={{ width: 200 }} />
        </div>
      );
    }

    // For Freight Forwarders: Always show company name and view profile
    if (isFreightForwarder) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '8px' }}>
          <span style={{ fontWeight: 500, marginRight: '12px' }}>{companyName}</span>
          <span
            onClick={() => navigateBusinessCard(userId)}
            style={{
              cursor: userId ? "pointer" : "not-allowed",
              fontWeight: 500,
              padding: '4px 8px',
              borderRadius: '4px',
              transition: 'all 0.2s',
              color: userId ? '#1890ff' : '#909090',
              textDecoration: userId ? 'underline' : 'none'
            }}
          >
            View Profile
          </span>
        </div>
      );
    }

    // For Exporters
    if (isExporter) {
      if (isFreeUser) {
        // Free Exporter: Show placeholder with upgrade option
        return (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '8px', 
            width: '100%' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ position: 'relative' }}>
                <span 
                  style={{ 
                    fontWeight: 500,
                    filter: 'blur(4px)',
                    userSelect: 'none',
                    background: 'linear-gradient(90deg, #d9d9d9 0%, #f0f0f0 50%, #d9d9d9 100%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    color: 'transparent',
                    display: 'inline-block',
                    minWidth: '120px'
                  }}
                >
                  {companyName}
                </span>
                <span style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '10px',
                  color: '#666',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  background: 'rgba(255,255,255,0.8)',
                  padding: '2px 6px',
                  borderRadius: '10px'
                }}>
                  Premium Feature
                </span>
              </div>
            </div>
            <Tag 
              color="gold" 
              style={{ 
                cursor: 'pointer',
                padding: '6px 16px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                width: 'fit-content',
                border: '1px solid #faad14'
              }}
              onClick={goToProfilePage}
            >
              <CrownOutlined /> UPGRADE TO VIEW COMPANY DETAILS
            </Tag>
          </div>
        );
      } else if (isPremiumUser) {
        // Premium Exporter: Full visibility with View Profile link
        return (
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontWeight: 500, marginRight: '12px' }}>{companyName}</span>
            <span
              onClick={() => navigateBusinessCard(userId)}
              style={{
                cursor: userId ? "pointer" : "not-allowed",
                fontWeight: 500,
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s',
                color: userId ? '#1890ff' : '#909090',
                textDecoration: userId ? 'underline' : 'none'
              }}
            >
              View Profile
            </span>
          </div>
        );
      }
    }
    
    // Default case (while loading or for other business types)
    // Show a placeholder instead of the actual company name
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        color: '#999',
        fontStyle: 'italic'
      }}>
        <span>Loading company information...</span>
      </div>
    );
  };

  const renderFreightForwarder = (data) => {
    return <CompanyNameDisplay data={data} />;
  };

  const userFreightRates = state?.userFreightRates || allFreightRates;
  console.log("userFreightRates", userFreightRates);

  // Helper function to format freight rate
  const formatFreightRate = (amount, currency) => {
    const formattedAmount = new Intl.NumberFormat('en-IN').format(amount);
    return `${currency} ${formattedAmount}`;
  };

  const renderContent = (data) => {
    if (!data) return null;

    // Check if we should hide the Company Name field
    const shouldHideCompanyName = isLoggedInUserFreightForwarder;

    return (
      <>
        <Descriptions column={1} bordered>
          <Descriptions.Item label="Posted Date" labelStyle={{ fontWeight: "bold" }}>
            {data.createdAt ? 
              format(new Date(data.createdAt), 'dd-MMM-yyyy') : '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Loading Port" labelStyle={{ fontWeight: "bold" }}>
            {data.loadingPort}
          </Descriptions.Item>

          <Descriptions.Item label="Destination Port" labelStyle={{ fontWeight: "bold" }}>
            {data.destinationPort}
          </Descriptions.Item>

          <Descriptions.Item label="Container Type" labelStyle={{ fontWeight: "bold" }}>
            {data.containerType}
          </Descriptions.Item>

          <Descriptions.Item label="Freight Rate" labelStyle={{ fontWeight: "bold" }}>
            {formatFreightRate(data.oceanfreight, data.currency)}
          </Descriptions.Item>

          <Descriptions.Item label="Price Validity" labelStyle={{ fontWeight: "bold" }}>
            {data.pricevalidity ? 
              format(new Date(data.pricevalidity), 'dd-MMM-yyyy') : '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Expected Departure Date" labelStyle={{ fontWeight: "bold" }}>
            {data.expectedDepartureDate ? 
              format(new Date(data.expectedDepartureDate), 'dd-MMM-yyyy') : '-'}
          </Descriptions.Item>

          <Descriptions.Item label="Transit Days" labelStyle={{ fontWeight: "bold" }}>
            {data.transitDays}
          </Descriptions.Item>

          <Descriptions.Item label="Comments" labelStyle={{ fontWeight: "bold" }}>
            {data.comments || '-'}
          </Descriptions.Item>

          {/* Conditionally render Company Name field only if user is NOT a freight forwarder */}
          {!shouldHideCompanyName && (
            <Descriptions.Item label="Company Name" labelStyle={{ fontWeight: "bold" }}>
              {renderFreightForwarder(data)}
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Show Book and Negotiate buttons only for Exporters */}
        {isLoggedInUserExporter && (
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            justifyContent: 'center',
            marginTop: '24px',
            padding: '16px',
            borderTop: '1px solid #f0f0f0'
          }}>
            <Button
              type="primary"
              size="large"
              onClick={() => handleRequestToBook(data)}
              style={{
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                fontWeight: '600',
                minWidth: '120px'
              }}
            >
              Book This Rate
            </Button>
            <Button
              type="default"
              size="large"
              onClick={() => handleRequestToNegotiate(data)}
              style={{
                backgroundColor: '#52c41a',
                borderColor: '#52c41a',
                color: 'white',
                fontWeight: '600',
                minWidth: '120px'
              }}
            >
              Negotiate
            </Button>
          </div>
        )}

        <hr />
        <div
          style={{
            justifyContent: isMobile ? "center" : "end",
            display: "flex",
            flex: 1,
            flexWrap: "wrap",
          }}
          className="gx-pt-4"
        >
          <Button
            type="secondary"
            size="large"
            className="submit-btn"
            style={{ alignItems: "center", alignContent: "center" }}
            onClick={() => {
              history.goBack();
            }}
          >
            Back
          </Button>
        </div>
      </>
    );
  };

  if (!freightRate && !userFreightRates) {
    return (
      <GTMWrapper>
        <Card
          className="myexim-detail-card"
          title={
            <div className="myexim-table-title gx-text-capitalize">
              <LeftOutlined
                className={'back-icon'}
                onClick={() => history.goBack()}
              />
              Freight Rate Details
            </div>
          }
        >
          <div style={{ padding: '20px', textAlign: 'center' }}>
            No freight rate data available
          </div>
        </Card>
      </GTMWrapper>
    );
  }

  return (
    <GTMWrapper>
      <Card
        className="myexim-detail-card"
        title={
          <div className="myexim-table-title gx-text-capitalize">
            <LeftOutlined
              className={'back-icon'}
              onClick={() => history.goBack()}
            />
            Freight Rate Details
          </div>
        }
      >
        {freightRate ? renderContent(freightRate) : renderContent(userFreightRates)}
      </Card>

      {/* Book Modal */}
      <Modal
        title="Request to Book Freight"
        open={isBookingModalVisible}
        onCancel={handleBookingCancel}
        footer={null}
        width={800}
        centered
      >
        <Form
          form={bookingForm}
          layout="vertical"
          onFinish={handleBookingSubmit}
          style={{ marginTop: 10 }}
        >
          <Form.Item
            label="Product Name"
            name="productName"
            rules={[
              { required: true, message: "Product name is required" },
            ]}
          >
            <Input
              placeholder="Enter product name"
              size="large"
              showCount
              maxLength={100}
            />
          </Form.Item>

          <Form.Item
            name="containerType"
            label="Container Type"
            rules={[{ required: true, message: 'Please select container type' }]}
          >
            <Select
              showSearch
              size="large"
              placeholder="Select Container Type"
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              <Option value={"20'drystandard"}>20' Dry Standard</Option>
              <Option value={"40'drystandard"}>40' Dry Standard</Option>
              <Option value={"40'dryhighcube"}>40' Dry High Cube</Option>
              <Option value={"45'dryhighcube"}>45' Dry High Cube</Option>
              <Option value={"20'reefer"}>20' Reefer</Option>
              <Option value={"40'reeferhighcube"}>40' Reefer High Cube</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="numberOfContainers"
            label="Number of Containers"
            rules={[{ required: true, message: 'Please enter number of containers' }]}
          >
            <Input 
              type="number" 
              min={1} 
              max={100}
              placeholder="Enter number of containers"
              size="large"
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
            <div style={{ flex: 1 }}>
              <Form.Item
                label="Net Weight Per Container (kg)"
                name="netWeightPerContainer"
                rules={[
                  { required: true, message: "Net weight is required" },
                  { 
                    type: 'number', 
                    min: 0, 
                    message: "Weight must be positive" 
                  }
                ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  min={0} 
                  style={{ width: "100%" }} 
                  size="large"
                  placeholder="Weight in kg"
                />
              </Form.Item>
            </div>
            
            <div style={{ flex: 1 }}>
              <Form.Item
                name="cargoReadinessDate"
                label="Cargo Readiness Date"
                rules={[{ required: true, message: 'Please select date' }]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker 
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="Select date"
                  disabledDate={(current) => current && current < moment().startOf('day')}
                />
              </Form.Item>
            </div>
          </div>

          <Form.Item
            name="specialRequirements"
            label="Special Requirements (Optional)"
          >
            <TextArea 
              rows={4}
              placeholder="Comments"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button 
              onClick={handleBookingCancel}
              style={{ marginRight: 10 }}
              size="large"
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={bookingLoading}
              size="large"
            >
              Submit Booking Request
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Negotiable Modal */}
      <Modal
        title="Request to Negotiate Freight"
        open={isNegotiableModalVisible}
        onCancel={handleNegotiableCancel}
        footer={null}
        width={800}
        centered
      >
        <Form
          form={negotiableForm}
          layout="vertical"
          onFinish={handleNegotiableSubmit}
          style={{ marginTop: 10 }}
        >
          <Form.Item
            label="Product Name"
            name="productName"
            rules={[
              { required: true, message: "Product name is required" },
            ]}
          >
            <Input
              placeholder="Enter product name"
              size="large"
              showCount
              maxLength={100}
            />
          </Form.Item>

          <Form.Item
            name="containerType"
            label="Container Type"
            rules={[{ required: true, message: 'Please select container type' }]}
          >
            <Select
              showSearch
              size="large"
              placeholder="Select Container Type"
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              <Option value={"20'drystandard"}>20' Dry Standard</Option>
              <Option value={"40'drystandard"}>40' Dry Standard</Option>
              <Option value={"40'dryhighcube"}>40' Dry High Cube</Option>
              <Option value={"45'dryhighcube"}>45' Dry High Cube</Option>
              <Option value={"20'reefer"}>20' Reefer</Option>
              <Option value={"40'reeferhighcube"}>40' Reefer High Cube</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="numberOfContainers"
            label="Number of Containers"
            rules={[{ required: true, message: 'Please enter number of containers' }]}
          >
            <Input 
              type="number" 
              min={1} 
              max={100}
              placeholder="Enter number of containers"
              size="large"
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
            <div style={{ flex: 1 }}>
              <Form.Item
                label="Net Weight Per Container (kg)"
                name="netWeightPerContainer"
                rules={[
                  { required: true, message: "Net weight is required" },
                  { 
                    type: 'number', 
                    min: 0, 
                    message: "Weight must be positive" 
                  }
                ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  min={0} 
                  style={{ width: "100%" }} 
                  size="large"
                  placeholder="Weight in kg"
                />
              </Form.Item>
            </div>
            
            <div style={{ flex: 1 }}>
              <Form.Item
                name="cargoReadinessDate"
                label="Cargo Readiness Date"
                rules={[{ required: true, message: 'Please select date' }]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker 
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="Select date"
                  disabledDate={(current) => current && current < moment().startOf('day')}
                />
              </Form.Item>
            </div>
          </div>

          {/* Rate Indication Field */}
          <Form.Item
            label="Rate Indication"
            required
            style={{ marginTop: 10 }}
          >
            <Input.Group 
              compact 
              style={{ 
                display: 'flex', 
                width: '100%',
                alignItems: 'center'
              }}
            >
              <Form.Item
                name="currency"
                style={{ 
                  margin: 0,
                  width: '30%',
                  marginRight: '8px'
                }}
                rules={[{ required: true, message: "Currency is required" }]}
              >
                <Select
                  showSearch
                  size="large"
                  placeholder="Select Currency"
                  style={{ width: '100%' }}
                >
                  {countries
                    ?.filter((country, index) => 
                      index === countries.findIndex((o) => country.currencyCode === o.currencyCode)
                    )
                    .map(
                      (option) =>
                        option.currencyCode && (
                          <Option key={option.id} value={option.currencyCode}>
                            {option.currencyCode}
                          </Option>
                        )
                    )}
                </Select>
              </Form.Item>
              <Form.Item
                name="rateindication"
                style={{ 
                  margin: 0,
                  flex: 1
                }}
                rules={[{ required: true, message: "Rate indication is required" }]}
              >
                <Input
                  type="number"
                  min={0}
                  placeholder="Enter your proposed rate"
                  size="large"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item
            name="specialRequirements"
            label="Special Requirements (Optional)"
          >
            <TextArea 
              rows={4}
              placeholder="Comments or negotiation details"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button 
              onClick={handleNegotiableCancel}
              style={{ marginRight: 10 }}
              size="large"
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={negotiableLoading}
              size="large"
              style={{
                backgroundColor: '#52c41a',
                borderColor: '#52c41a'
              }}
            >
              Submit Negotiation Request
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </GTMWrapper>
  );
};

export default UpdatefreightrateDetails;